import React from 'react';
import { Link, LinkProps, useNavigate } from 'react-router-dom';

import { Box, Card, CardMedia, CardProps, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { Waves, Wrench } from 'lucide-react';

import { RouteStopListItem } from '../../../apis/serviceHistoryApi';
import { convertUTCDateToLocalDate, formatDateShort } from '../../../utils/formatUtils';

const cardIconBgColors = {
  poolService: '#29B6F6',
  workOrder: '#26A69A',
  skippedStop: '#78909C',
};

const StyledLink = styled(Link)<LinkProps>`
  color: ${(props) => props.theme.palette.primary.light};
  text-decoration: none;

  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

const TypographyEllipses = styled(Typography)`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type ServiceHistoryCardProps = CardProps & { row: RouteStopListItem };

const ServiceHistoryCard: React.FC<ServiceHistoryCardProps> = ({ row }) => {
  const navigate = useNavigate();

  const handleCardClicked = (row: RouteStopListItem, evt: object) => {
    if (!row.isSkipped) {
      if (row.isStop) {
        navigate(`stops/${row.id}`);
      } else {
        navigate(`work-orders/${row.id}`);
      }
    }
    return evt;
  };

  return (
    <Card
      onClick={(evt) => handleCardClicked(row, evt)}
      title={row.isSkipped ? (row.skippedReason ? `Reason: ${row.skippedReason}` : '') : 'Press to view'}
      sx={{
        m: 2,
        display: 'flex',
        border: '1px solid',
        cursor: () => (!row.isSkipped ? 'pointer' : null),
        borderColor: (theme) => theme.palette.divider,
        '&:hover': {
          borderColor: (theme) => (!row.isSkipped ? theme.palette.action.active : null),
        },
      }}
    >
      <Box
        sx={{
          width: 95,
          minWidth: 95,
          height: 95,
          overflow: 'hidden',
        }}
      >
        {row.photoUrl && (
          <CardMedia
            component="img"
            image={row.photoUrl}
            alt="Photo from route stop"
            height="100%"
            width="100%"
            sx={{ objectFit: 'cover', overflow: 'hidden' }}
          />
        )}
        {!row.photoUrl && (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={(theme) => theme.palette.common.white}
            sx={{
              backgroundColor: () =>
                row.isStop ? (row.isSkipped ? cardIconBgColors.skippedStop : cardIconBgColors.poolService) : cardIconBgColors.workOrder,
            }}
          >
            {row.isStop && <Waves width="55%" height="55%" />}
            {!row.isStop && <Wrench width="60%" height="60%" />}
          </Box>
        )}
      </Box>
      <Box pt={3} pl={3} pr={1} pb={1} overflow="hidden" whiteSpace="nowrap" flexShrink={1} flexGrow={1}>
        <TypographyEllipses variant="h6">
          {row.isStop ? (row.isSkipped ? 'Unable to Service' : 'Pool Service') : 'Work Order'}:&nbsp;
          {formatDateShort(convertUTCDateToLocalDate(row.serviceDate))}
        </TypographyEllipses>
        <TypographyEllipses
          mt={1}
          variant="body1"
          sx={{
            fontSize: 14,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          {row.address}
        </TypographyEllipses>
        {(row.serviceTechFirstName || row.serviceTechLastName) && (
          <TypographyEllipses
            mt={1}
            variant="body1"
            sx={{
              fontSize: 14,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            Tech: {row.serviceTechFirstName} {row.serviceTechLastName}
          </TypographyEllipses>
        )}
      </Box>
      <Box width={20} pl={2} mr={3} fontSize={40} display="flex" alignItems="center">
        {!row.isStop && <StyledLink to={`/work-orders/${row.id}`}>&rsaquo;</StyledLink>}
        {row.isStop && !row.isSkipped && <StyledLink to={`/stops/${row.id}`}>&rsaquo;</StyledLink>}
        {row.isStop && row.isSkipped && <Box sx={{ display: 'inline', opacity: 0.3 }}>&rsaquo;</Box>}
      </Box>
    </Card>
  );
};

export default ServiceHistoryCard;
